import React, { useContext, useEffect, useState } from "react";
import { Container, Table, Button, Row, Col, Input } from "reactstrap";
import PaginationMaterial from "../../components/PaginationMaterial/PaginationMaterial";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Header from "../../components/Headers/Header";
import AdminNavbar from "../../components/Navbars/AdminNavbar/AdminNavbar";
import { AuthContext } from "../../context/AuthContext";
import { TerceiroContext } from "../../context/TerceiroContext";
import { TerceiroType, EditTerceiroType } from "../../context/hooks/useTerceiro";
import useUser from "../../context/hooks/useUser";
import { createPermissionChecker, PermissionModule } from "../../types/permissions";

import { CreateTerceiroModal } from "../../components/Terceiro/CreateTerceiroModal";
import { EditTerceiroModal } from "../../components/Terceiro/EditTerceiroModal";
import ModalConfirm from '../../components/ModalConfirm/ModalConfirm';

const Terceiro = () => {
    const { objUserTk } = useContext(AuthContext);
    const { getTerceiroPaged, deleteTerceiro, listTerceiro, pageCount } =
        useContext(TerceiroContext);

    const { getPermissionsByUser, userPermissions, useLegacyPermissions, modulePermissions } = useUser();
    const hasPermission = createPermissionChecker(useLegacyPermissions, userPermissions, modulePermissions);

    const [editingTerceiro, setEditingTerceiro] = useState<EditTerceiroType | null>(null);
    const [dataSearch, setDataSearch] = useState({ name: "", empresaId: 0 });
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);

    useEffect(() => {
        if (!objUserTk?.EmpresaId) return;

        getTerceiroPaged(1, "", objUserTk.EmpresaId, 9)
            .catch((error) => console.error("Erro ao buscar terceiros:", error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objUserTk?.EmpresaId]);

    useEffect(() => {
        if (!objUserTk?.UserId) return;
        
        getPermissionsByUser(objUserTk.UserId)
            .catch((error) => console.error("Erro ao carregar permissões:", error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objUserTk?.UserId]);

    const onChangePaginationCommon = async (_e: any, value: any) => {
        if (!objUserTk?.EmpresaId) return;
        await getTerceiroPaged(value, dataSearch.name, objUserTk.EmpresaId, 9);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDataSearch((prev) => ({ ...prev, name: e.target.value }));
    };

    const handleSearchButtonClick = () => {
        if (!objUserTk?.EmpresaId) return;
        getTerceiroPaged(1, dataSearch.name, objUserTk.EmpresaId, 9)
            .catch((error) => console.error("Erro ao buscar terceiros:", error));
    };

    const handleClearFilters = () => {
        if (!objUserTk?.EmpresaId) return;
        setDataSearch({ name: "", empresaId: 0 });
        getTerceiroPaged(1, "", objUserTk.EmpresaId, 9)
            .catch((error) => console.error("Erro ao limpar filtros:", error));
    };

    const handleEditTerceiro = (terceiro: TerceiroType) => {
        const updateData: EditTerceiroType = {
            id: terceiro.id,
            name: terceiro.name,
            email: terceiro.email,
            CPF: terceiro.cpf,
            telephone: terceiro.telephone,
            telephone_01: terceiro.telephone_01,
            address: terceiro.address,
            addressNumber: terceiro.addressNumber,
            neighborhood: terceiro.neighborhood,
            city: terceiro.city,
            state: terceiro.state,
            zipCode: terceiro.zipCode,
            landline: terceiro.landline,
            bank: terceiro.bank,
            agency: terceiro.agency,
            currentAccount: terceiro.currentAccount,
            observation: terceiro.observation,
            pix: terceiro.pix,
            balanceExpenses: terceiro.balanceExpenses,
            spendingCeiling: terceiro.spendingCeiling,
            isManager: terceiro.isManager,
            password: "",
            tipoTerceiroId: terceiro.tipoTerceiroId,
        };

        setEditingTerceiro(updateData);
        setEditModalOpen(true);
    };

    const [confirmDeleteTerceiroId, setConfirmDeleteTerceiroId] = useState<number | null>(null);

    const handleDeleteTerceiro = (terceiroId: number) => {
        if (!objUserTk?.EmpresaId) return;
        setConfirmDeleteTerceiroId(terceiroId);
    };

    const executeDeleteTerceiro = () => {
        if (confirmDeleteTerceiroId == null || !objUserTk?.EmpresaId) return;
        deleteTerceiro(confirmDeleteTerceiroId)
            .then(() => getTerceiroPaged(1, "", objUserTk.EmpresaId, 9))
            .catch((error) => console.error("Erro ao excluir terceiro:", error));
        setConfirmDeleteTerceiroId(null);
    };

    const canView = hasPermission(PermissionModule.Terceiros, 2);
    const canCreate = hasPermission(PermissionModule.Terceiros, 1);
    const canEdit = hasPermission(PermissionModule.Terceiros, 3);
    const canDelete = hasPermission(PermissionModule.Terceiros, 4);

    return (
        <>
            <LoadingSpinner />

            <Header />
            <AdminNavbar brandText={"Gerenciar Terceiros"} search={false} titleSearch={""} />

            <Container className="mt-5">
                <Row className="align-items-center mb-4">
                    <Col xs="12" md="9" lg="9" className="mb-2">
                        <Input
                            className="form-control-alternative"
                            type="text"
                            name="search"
                            id="search"
                            placeholder="Buscar por nome"
                            value={dataSearch.name}
                            onChange={handleSearchChange}
                        />
                    </Col>

                    <Col
                        xs="12"
                        md="3"
                        lg="3"
                        className="d-flex justify-content-start justify-content-md-end mb-2"
                    >
                        <Button
                            style={{ color: "#FFF", backgroundColor: "#585858" }}
                            type="button"
                            size="m"
                            onClick={handleSearchButtonClick}
                            className="mr-2"
                            disabled={!objUserTk?.EmpresaId}
                        >
                            Buscar
                        </Button>
                        <Button
                            style={{ color: "#FFF", backgroundColor: "#d9534f" }}
                            type="button"
                            size="m"
                            onClick={handleClearFilters}
                            disabled={!objUserTk?.EmpresaId}
                        >
                            Limpar
                        </Button>
                    </Col>

                    {canCreate && (
                        <Col xs="12" className="text-right mt-3">
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#585858" }}
                                size="m"
                                onClick={() => setCreateModalOpen(true)}
                                disabled={!objUserTk?.EmpresaId}
                            >
                                Adicionar terceiro
                            </Button>
                        </Col>
                    )}
                </Row>

                {canView ? (
                    <Row className="mt-4">
                        <Col>
                            <Table hover responsive className="table-striped">
                                <thead>
                                    <tr>
                                        <th>Sequência</th>
                                        <th>Nome</th>
                                        <th>Email</th>
                                        <th>Tipo de terceiro</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listTerceiro.map((terceiro: TerceiroType) => (
                                        <tr key={terceiro.id}>
                                            <td>{terceiro.id}</td>
                                            <td>{terceiro.name}</td>
                                            <td>{terceiro.email}</td>
                                            <td>{terceiro.tipoTerceiroName || "Não definida"}</td>
                                            <td>
                                                {canEdit && (
                                                    <Button
                                                        onClick={() => handleEditTerceiro(terceiro)}
                                                    >
                                                        <i className="fa-solid fa-pen-to-square"></i>
                                                    </Button>
                                                )}
                                                {canDelete && (
                                                    <Button
                                                        onClick={() => handleDeleteTerceiro(terceiro.id)}
                                                        color="danger"
                                                        className={canEdit ? "ml-2" : ""}
                                                    >
                                                        <i className="fa-solid fa-trash"></i>
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <div className="d-flex justify-content-end mt-3">
                                {pageCount ? (
                                    <div className="d-flex justify-content-center mt-4">
                                        <PaginationMaterial
                                            onChange={(e: any, value: any) => onChangePaginationCommon(e, value)}
                                            count={pageCount}
                                        />
                                    </div>
                                ) : null}

                                {listTerceiro.length === 0 && (
                                    <div style={{ color: "red" }}>NÃO FOI ENCONTRADO NENHUM REGISTRO</div>
                                )}
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <Row className="mt-4">
                        <Col>
                            <div style={{ color: "red", textAlign: "center" }}>
                                Você não tem permissão para visualizar os terceiros.
                            </div>
                        </Col>
                    </Row>
                )}
            </Container>

            <CreateTerceiroModal
                isOpen={createModalOpen}
                toggle={() => setCreateModalOpen(false)}
                onCreated={() => {
                    if (!objUserTk?.EmpresaId) return;
                    getTerceiroPaged(1, "", objUserTk.EmpresaId, 9);
                }}
            />

            <EditTerceiroModal
                isOpen={editModalOpen}
                toggle={() => setEditModalOpen(false)}
                terceiro={editingTerceiro}
                onUpdated={() => {
                    if (!objUserTk?.EmpresaId) return;
                    getTerceiroPaged(1, "", objUserTk.EmpresaId, 9);
                }}
            />
        </>
    );
};

export default Terceiro;